import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { MercerSupportHelper } from '../../helpers/mercer-support.helper';

enum PositionType {
  Derived = 'Derived',
  Proposed = 'Proposed',
  Adjusted = 'Adjusted',
  SuperiorPC = 'SuperiorPC',
  AdjustedDerived = 'AdjustedDerived'
}

interface BarConfig {
  positionClass: number;
  type?: PositionType;
  label?: string;
}

@Component({
  selector: 'coin-c-position-class-bar',
  templateUrl: './position-class-bar.component.html',
  styleUrls: ['./position-class-bar.component.scss'],
  standalone: false
})
export class PositionClassBarComponent implements OnChanges {
  @Input() derivedPositionClass?: number;
  @Input() adjustedPositionClass?: number;
  @Input() superiorPositionClass?: number;

  @Input() proposedPositionClass?: number; // TODO: needed for the manager workflow which is currently not implemented

  public barConfigurations: BarConfig[] = [];
  public seniorManagementPositionClassStart = MercerSupportHelper.seniorManagementPositionClassStart;

  private defaultPositionClassPosition = 4;
  private positionClassBarLength = 8;
  private minPositionClass = 1;
  private maxPositionClass = MercerSupportHelper.maxPositionClass;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.derivedPositionClass ||
      changes.proposedPositionClass ||
      changes.superiorPositionClass ||
      changes.adjustedPositionClass
    ) {
      this.setupBarConfigurations();
    }
  }

  public barRange(configs: BarConfig[]): { min: number; max: number } {
    const positionClasses = configs.map(({ positionClass }) => positionClass);
    return { min: Math.min(...positionClasses), max: Math.max(...positionClasses) };
  }

  private setupBarConfigurations(): void {
    const positionClassForCalculation =
      this.adjustedPositionClass ?? this.derivedPositionClass ?? this.superiorPositionClass;
    let firstPositionClass =
      positionClassForCalculation > this.defaultPositionClassPosition
        ? positionClassForCalculation - this.defaultPositionClassPosition + this.minPositionClass
        : this.minPositionClass;

    // if position class range goes beyond max position class, start from highest possible position class
    if (firstPositionClass + this.positionClassBarLength - this.minPositionClass > this.maxPositionClass) {
      firstPositionClass = this.maxPositionClass - this.positionClassBarLength + this.minPositionClass;
    }

    this.barConfigurations = [];

    for (let i = 0; i < this.positionClassBarLength; i++) {
      const positionClass = firstPositionClass + i;
      const { type, label } = this.getTypeBasedOnPositionClass(positionClass);

      this.barConfigurations.push({
        positionClass,
        type,
        label
      });
    }
  }

  private getTypeBasedOnPositionClass(positionClass: number): { type: PositionType; label: string } {
    let type: PositionType;

    if (this.derivedPositionClass === positionClass && this.adjustedPositionClass === positionClass) {
      type = PositionType.AdjustedDerived;
    } else if (this.derivedPositionClass === positionClass) {
      type = PositionType.Derived;
    } else if (this.adjustedPositionClass === positionClass) {
      type = PositionType.Adjusted;
    } else if (this.proposedPositionClass === positionClass) {
      type = PositionType.Proposed;
    } else if (this.superiorPositionClass === positionClass) {
      type = PositionType.SuperiorPC;
    }

    return {
      type,
      label: type ? `mercer-support.dialog.${TinyHelpers.pascalcaseToKebabCase(type)}` : null
    };
  }
}
